/**
 * Bullet and numbered lists.
 *
 * Toggling is expressed as two phases -- flatten every touched block back to a
 * paragraph, then (when switching on) wrap the resulting run into a list. That
 * makes `ul -> ol` fall out of the same code path as `p -> ul` and `ul -> p`,
 * instead of needing three separate algorithms.
 */

import * as bookmark from './bookmark';
import { blockOf, blocksInRange, ensureFillerBr, isList } from './dom-utils';
import { LIST_TAGS } from './schema';

export type ListType = 'UL' | 'OL';

/** The list type the selection sits in, if any. Drives `aria-pressed`. */
export function activeList(root: HTMLElement, range: Range): ListType | null {
  const block = blockOf(range.startContainer, root);
  if (!block || block.nodeName !== 'LI') {
    return null;
  }
  const parent = block.parentElement;
  if (!parent || !LIST_TAGS.has(parent.nodeName)) {
    return null;
  }
  return parent.nodeName as ListType;
}

/**
 * Converts a list item back into a root-level paragraph, splitting the list
 * around it so the items before and after keep their own lists.
 */
export function outdentItem(item: Element): Element {
  const list = item.parentElement;
  if (!list || !LIST_TAGS.has(list.nodeName)) {
    return item;
  }

  const paragraph = document.createElement('p');
  while (item.firstChild) {
    paragraph.appendChild(item.firstChild);
  }
  ensureFillerBr(paragraph);

  const following: Element[] = [];
  let sibling = item.nextElementSibling;
  while (sibling) {
    following.push(sibling);
    sibling = sibling.nextElementSibling;
  }

  list.after(paragraph);
  item.remove();

  if (following.length > 0) {
    const tail = document.createElement(list.nodeName.toLowerCase());
    for (const rest of following) {
      tail.appendChild(rest);
    }
    paragraph.after(tail);
  }

  if (list.children.length === 0) {
    list.remove();
  }

  return paragraph;
}

/** Merges a list with an identical-type neighbour on either side. */
function mergeWithNeighbours(list: Element): void {
  const previous = list.previousElementSibling;
  if (previous && previous.nodeName === list.nodeName) {
    while (list.firstChild) {
      previous.appendChild(list.firstChild);
    }
    list.remove();
    mergeWithNeighbours(previous);
    return;
  }

  const next = list.nextElementSibling;
  if (next && next.nodeName === list.nodeName) {
    while (next.firstChild) {
      list.appendChild(next.firstChild);
    }
    next.remove();
    mergeWithNeighbours(list);
  }
}

/** Wraps contiguous runs of paragraphs into lists of the given type. */
function wrapParagraphs(paragraphs: readonly Element[], type: ListType): void {
  const tag = type.toLowerCase();
  const created: Element[] = [];
  let list: Element | null = null;

  for (const paragraph of paragraphs) {
    const continuesRun = list !== null && paragraph.previousElementSibling === list;
    if (!continuesRun) {
      list = document.createElement(tag);
      created.push(list);
      paragraph.before(list);
    }

    const item = document.createElement('li');
    while (paragraph.firstChild) {
      item.appendChild(paragraph.firstChild);
    }
    ensureFillerBr(item);
    list!.appendChild(item);
    paragraph.remove();
  }

  for (const entry of created) {
    if (entry.isConnected) {
      mergeWithNeighbours(entry);
    }
  }
}

/**
 * Toggles the selection into or out of a list. Every touched block is flattened
 * to a paragraph first; when the selection was not already entirely in a list
 * of this type, the flattened run is then wrapped.
 */
export function toggleList(root: HTMLElement, range: Range, type: ListType): void {
  const mark = bookmark.save(root);
  const blocks = blocksInRange(root, range);
  if (blocks.length === 0) {
    return;
  }

  const alreadyInTarget = blocks.every(
    (block) => block.nodeName === 'LI' && block.parentElement?.nodeName === type,
  );

  const paragraphs = blocks.map((block) => (block.nodeName === 'LI' ? outdentItem(block) : block));

  if (!alreadyInTarget) {
    wrapParagraphs(paragraphs, type);
  }

  bookmark.restore(root, mark);
}

/**
 * Enter inside an empty list item leaves the list rather than adding another
 * empty bullet -- and on a soft keyboard it is the only way out of one.
 */
export function outdentEmptyItem(root: HTMLElement, item: Element): void {
  const mark = bookmark.save(root);
  outdentItem(item);
  bookmark.restore(root, mark);
}

/** True when the node is a list item holding no text. */
export function isEmptyListItem(node: Node | null, root: HTMLElement): Element | null {
  const block = blockOf(node, root);
  if (!block || block.nodeName !== 'LI') {
    return null;
  }
  return (block.textContent ?? '').trim().length === 0 && isList(block.parentElement)
    ? block
    : null;
}
